import type {
  CourseAttributes,
  LearnerFeedbackAttributes,
  SeriesAttributes,
  SeriesHostAttributes,
  SeriesRegistrationAttributes,
  SeriesReservationAttributes,
  SeriesTemplateAttributes,
  SessionAttributes,
  SessionRegistrationAttributes,
  UserAttributes,
  WorldAttributes,
} from '@/server/models'

import type { UserContextType } from '@/client/context/user'
import { SQL, type SQLFragment } from '@/client/query'
import { LEARNER_FEEDBACK_TYPE_TUTOR_TO_LEARNER } from '@/client/services/learnerFeedback/constants'
import type { ProgramIteration, ProgramOfferingInstance } from '@/client/services/programs/types'
import { SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS } from '@/client/services/satBootcamp'
import type { SeriesTag as SeriesTagType } from '@/client/services/tags'
import { RECURRING_SERIES_TEMPLATE_SCHEDULE_TYPES } from '@/client/services/template/constants'
import type { WorldVisibility } from '@/client/services/worlds'

import CourseQ from './CourseQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import {
  DIALOGUE_CLUB_TEMPLATE_SLUGS,
  DIALOGUE_EVENT_TEMPLATE_SLUG,
  type GatingGroup,
  type HostRole,
  type ResearchGroup,
  type SATScoreBandName,
  SessionAttendeeRole,
} from './constants'
import LearnerFeedbackQ from './LearnerFeedbackQ'
import SeriesHostQ from './SeriesHostQ'
import SeriesRegistrationQ from './SeriesRegistrationQ'
import SeriesReservationQ from './SeriesReservationQ'
import SeriesTagQ from './SeriesTagQ'
import SeriesTemplateQ from './SeriesTemplateQ'
import SessionAttendeeQ from './SessionAttendeeQ'
import SessionFeedbackQ from './SessionFeedbackQ'
import SessionQ from './SessionQ'
import SessionRegistrationQ from './SessionRegistrationQ'
import UserQ from './UserQ'
import WorldMemberQ from './WorldMemberQ'
import WorldQ from './WorldQ'

const SeriesQ = {
  ...makeQueryModelV2<SeriesAttributes>('series')([
    'id',
    'user_id',
    'course_id',
    'world_id',
    'title',
    'description',
    'questionnaire',
    'qualifications',
    'attendance_policy',
    'languages',
    'max_learners',
    'enrollment_closed_at',
    'closed_at',
    'closed_reason',
    'enrollment_style',
    'created_at',
    'updated_at',
    'deleted_at',
    'changelog',
    'internal_state',
    'allow_cohosts',
    'series_template_id',
  ]),

  associations: (): AssociationDefinitions => ({
    SeriesHost: {
      query: SeriesHostQ,
      foreignKey: SeriesHostQ.series_id,
      sourceKey: SeriesQ.id,
    },
    Course: {
      query: CourseQ,
      foreignKey: CourseQ.id,
      sourceKey: SeriesQ.course_id,
    },
    World: {
      query: WorldQ,
      foreignKey: WorldQ.id,
      sourceKey: SeriesQ.world_id,
    },
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: SeriesQ.user_id,
      // Include soft-deleted users by default, since we keep Series
      // rows even after associated user is deleted.
      paranoid: false,
    },
    SeriesRegistration: {
      query: SeriesRegistrationQ,
      foreignKey: SeriesRegistrationQ.series_id,
      sourceKey: SeriesQ.id,
    },
    SeriesTemplate: {
      query: SeriesTemplateQ,
      foreignKey: SeriesTemplateQ.id,
      sourceKey: SeriesQ.series_template_id,
    },
    Sessions: {
      query: SessionQ,
      foreignKey: SessionQ.series_id,
      sourceKey: SeriesQ.id,
    },
    SeriesTag: {
      query: SeriesTagQ,
      foreignKey: SeriesTagQ.series_id,
      sourceKey: SeriesQ.id,
    },
  }),

  hasUpcomingSessionWithAnyTopicId: (topicIds: number[]): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionQ.where(
      { seriesId: SeriesQ.id },
      SQL`${SessionQ.topic_id} = ANY(${topicIds}) AND ${SessionQ.start_date} > NOW()`
    )})`,

  getFirstRegistrationDate: (): SQLFragment<string> =>
    SQL`(SELECT MIN(${SeriesRegistrationQ.created_at}) ${SeriesRegistrationQ.where({
      seriesId: SeriesQ.id,
    })})`,

  countSessionReschedulesAfterFirstRegistration: (): SQLFragment<number> =>
    SQL`SUM(${SessionQ.countReschedules({
      minTimestampQuery: SeriesQ.getFirstRegistrationDate(),
    })}) ${SessionQ.where({
      seriesId: SeriesQ.id,
    })}`,

  hasUpcomingSessionWithTopicId: (topicId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionQ.where(
      {
        seriesId: SeriesQ.id,
        topicId,
      },
      SQL`${SessionQ.start_date} > NOW()`
    )})`,

  isReservedByFriend: (userId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SeriesReservationQ.where(
      {
        seriesId: SeriesQ.id,
        userId,
      },
      SQL`${SeriesReservationQ.reserver_user_id} != ${userId}`
    )})`,

  where: (
    {
      id,
      userId,
      courseId,
      worldId,
      seriesTemplateId,
      paranoid = true,
      enrollmentOpen = null,
      includeAllResearchGroups = false,
      hideGatedSeries = false,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      id?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      courseId?: number | null | SQLFragment<number>
      worldId?: number | null | SQLFragment<number>
      seriesTemplateId?: number | null | SQLFragment<number>
      paranoid?: boolean
      enrollmentOpen?: boolean | null
      includeAllResearchGroups?: boolean
      hideGatedSeries?: boolean
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SeriesAttributes> => SQL`
        FROM ${SeriesQ}
        ${includeAssociationsToSql(SeriesQ, include)}
        WHERE
            ${id != null ? SQL`${SeriesQ.id} = ${id} AND` : SQL``}
            ${userId != null ? SQL`${SeriesQ.user_id} = ${userId} AND` : SQL``}
            ${courseId != null ? SQL`${SeriesQ.course_id} = ${courseId} AND` : SQL``}
            ${worldId != null ? SQL`${SeriesQ.world_id} = ${worldId} AND` : SQL``}
            ${
              seriesTemplateId != null
                ? SQL`${SeriesQ.series_template_id} = ${seriesTemplateId} AND`
                : SQL``
            }
            ${paranoid ? SQL`${SeriesQ.deleted_at} IS NULL AND` : SQL``}
            ${includeAllResearchGroups ? SQL`` : SQL`${SeriesQ.getResearchGroup()} IS NULL AND`}
            ${hideGatedSeries ? SQL`${SeriesQ.getGatingGroup()} IS NULL AND` : SQL``}
            ${
              enrollmentOpen != null
                ? SQL`${enrollmentOpen ? SQL`NOT ` : SQL``} ${SeriesQ.isEnrollmentClosed()} AND`
                : SQL``
            }
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,

  getCourse: (): SQLFragment<CourseAttributes> =>
    SQL`FROM ${CourseQ} WHERE ${CourseQ.id} = ${SeriesQ.course_id}`,

  getWorld: (): SQLFragment<WorldAttributes> => WorldQ.where({ id: SeriesQ.world_id }),

  getSessionsWithRegistrations: (): SQLFragment<SessionRegistrationAttributes> => SQL`
        ${SessionRegistrationQ.where(
          {
            includeSession: true,
          },
          SQL`${SessionQ.series_id} = ${SeriesQ.id}`
        )}
    `,

  getNextSessionStartDate: (): SQLFragment<SessionAttributes> =>
    SQL`(SELECT ${SessionQ.start_date} ${SeriesQ.getNextSession()})`,

  getNextSession: (): SQLFragment<SessionAttributes> => SQL`
        ${SessionQ.where(
          {
            seriesId: SeriesQ.id,
            order: [[SessionQ.start_date, 'asc']],
          },
          SQL`${SessionQ.end_date} > CURRENT_TIMESTAMP`
        )} LIMIT 1`,

  getMostRecentSession: ({
    include,
  }: {
    include?: IncludeAssociations
  }): SQLFragment<SessionAttributes> => SQL`
        ${SessionQ.where(
          {
            seriesId: SeriesQ.id,
            order: [[SessionQ.start_date, 'desc']],
            include,
          },
          SQL`${SessionQ.start_date} < CURRENT_TIMESTAMP`
        )} LIMIT 1`,

  getMostRecentSessionStartDate: (): SQLFragment<SessionAttributes> =>
    SQL`(SELECT ${SessionQ.start_date} ${SeriesQ.getMostRecentSession({})})`,

  getRegistrations: (
    {
      userId,
      paranoid = true,
      include,
      order = null,
    }: {
      userId?: number | null | SQLFragment<number>
      paranoid?: boolean
      include?: IncludeAssociations
      order?: QueryOrder | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SeriesRegistrationAttributes> => SQL`
        ${SeriesRegistrationQ.where(
          {
            userId,
            seriesId: SeriesQ.id,
            include,
            paranoid,
            order: order ?? [[SeriesRegistrationQ.created_at, 'desc']],
          },
          customWhere
        )}
    `,
  getCurrentMembers: (): SQLFragment<UserAttributes> => SQL`
        ${UserQ.where({})}
        AND ${UserQ.id} IN
        (
            SELECT
                ${SeriesRegistrationQ.user_id}
            ${SeriesQ.getRegistrations(
              {},
              SQL`(NOT (SELECT ${SeriesRegistrationQ.isWithdrawn()}))`
            )}
        )
    `,
  getPastMembers: (): SQLFragment<UserAttributes> => SQL`
        ${UserQ.where({})}
        AND ${UserQ.id} IN
        (
            SELECT
                DISTINCT(${SeriesRegistrationQ.user_id})
            ${SeriesQ.getRegistrations(
              { paranoid: false, order: [] },
              SQL`${SeriesRegistrationQ.user_id} NOT IN (SELECT ${
                UserQ.id
              } ${SeriesQ.getCurrentMembers()})`
            )}
        )
    `,

  getFirstSession: (): SQLFragment<SessionAttributes> =>
    SQL`${SessionQ.where({
      id: SQL`series_first_session_id(${SeriesQ.id})`,
    })}`,
  getLastSession: (): SQLFragment<SessionAttributes> =>
    SQL`${SessionQ.where({
      id: SQL`series_last_session_id(${SeriesQ.id})`,
    })}`,

  getComputedStartDate: (): SQLFragment<string> =>
    SQL`${SessionQ.start_date} ${SeriesQ.getFirstSession()}`,

  getComputedEndDate: (): SQLFragment<Date> =>
    SQL`${SessionQ.end_date} ${SeriesQ.getLastSession()}`,

  hasEnded: (): SQLFragment<boolean> => SQL`(${SeriesQ.closed_at} <= NOW())`,
  hasUpcomingSession: (): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionQ.where(
      {
        seriesId: SeriesQ.id,
      },
      SQL`${SessionQ.end_date} > CURRENT_TIMESTAMP`
    )})`,
  getUpcomingSessions: ({
    order = null,
    limit = null,
  }: {
    order?: QueryOrder | null
    limit?: number | null
  }): SQLFragment<SessionAttributes> =>
    SeriesQ.getSessions(
      { order: order ?? [[SessionQ.start_date, 'asc']], limit },
      SessionQ.upcomingSessionsFilter()
    ),

  getSessions: (
    {
      order = null,
      limit = null,
      include = [],
    }: {
      order?: QueryOrder | null
      limit?: number | null
      include?: IncludeAssociations
    } = {},
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SessionAttributes> => SQL`
        ${SessionQ.where(
          {
            seriesId: SeriesQ.id,
            order: order ?? [[SessionQ.start_date, 'asc']],
            limit,
            include,
          },
          customWhere
        )}
    `,

  countSessions: (): SQLFragment<number> =>
    SQL`COUNT(*)::integer ${SessionQ.where({
      seriesId: SeriesQ.id,
    })}
    `,

  getUser: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: SeriesQ.user_id,
      paranoid: false,
    }),

  getActiveSelfReservations: (
    userId: number | SQLFragment<number>
  ): SQLFragment<SeriesReservationAttributes> =>
    SQL`${SeriesReservationQ.where(
      {
        userId,
        reserverUserId: userId,
        seriesId: SeriesQ.id,
      },
      SQL`${SeriesReservationQ.expired_at} > NOW()`
    )}`,

  countRegistrations: (customWhere?: SQLFragment<unknown> | null): SQLFragment<number> =>
    SQL`count(*)::integer ${SeriesRegistrationQ.where(
      {
        seriesId: SeriesQ.id,
      },
      customWhere
    )}
    `,

  worksWithDays: ({
    days,
    timezone,
  }: {
    days: number[]
    timezone: string
  }): SQLFragment<boolean> => SQL`NOT EXISTS(SELECT 1
      ${SessionQ.where(
        { seriesId: SeriesQ.id },
        SQL`NOT (EXTRACT(DOW FROM ${SessionQ.start_date} AT TIME ZONE ${timezone}) = ANY(${days}))`
      )}
    )`,

  worksWithTimes: ({
    timeMin,
    timeMax,
    timezone,
  }: {
    timeMin: number
    timeMax: number
    timezone: string
  }): SQLFragment<boolean> => SQL`NOT EXISTS(
    SELECT 1 ${SessionQ.where(
      {
        seriesId: SeriesQ.id,
      },
      SQL`EXTRACT(HOUR FROM ${SessionQ.start_date} AT TIME ZONE ${timezone})
        NOT BETWEEN ${timeMin} AND ${timeMax - 1}`
    )}
  )`,

  countRegistrationsAndActiveReservations: (): SQLFragment<number> =>
    SQL`((SELECT ${SeriesQ.countRegistrations()}) + (SELECT ${SeriesQ.countActiveReservations()}))`,

  hasRoomForLearners: (): SQLFragment<boolean> =>
    SQL`(${SeriesQ.max_learners} > ${SeriesQ.countRegistrationsAndActiveReservations()})`,

  getActiveReservations: ({
    reserverUserId = null,
    userIdToExclude = null,
    includeUser = false,
  }: {
    reserverUserId?: number | null
    userIdToExclude?: SQLFragment<number> | number | null
    includeUser?: boolean
  } = {}): SQLFragment<SeriesReservationAttributes> =>
    SQL`${SeriesReservationQ.where(
      {
        seriesId: SeriesQ.id,
        reserverUserId,
        include: includeUser
          ? [
              {
                association: 'User',
                required: true,
              },
            ]
          : [],
      },
      SQL`${SeriesReservationQ.expired_at} > NOW() AND
        ${
          userIdToExclude == null
            ? 'TRUE'
            : SQL`(${SeriesReservationQ.user_id} IS NULL OR ${SeriesReservationQ.user_id} != ${userIdToExclude})`
        }`
    )}`,

  countActiveReservations: ({
    reserverUserId,
    userIdToExclude,
  }: {
    reserverUserId?: number | null
    userIdToExclude?: SQLFragment<number> | number | null
  } = {}): SQLFragment<number> =>
    SQL`COUNT(*)::integer ${SeriesQ.getActiveReservations({ reserverUserId, userIdToExclude })}`,

  isUserRegisteredOrSelfReserved: (userId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`(
      EXISTS(SELECT 1 ${SeriesRegistrationQ.where({
        seriesId: SeriesQ.id,
        userId,
      })})
      OR
      EXISTS(SELECT 1 ${SeriesReservationQ.where(
        {
          seriesId: SeriesQ.id,
          userId,
          reserverUserId: userId,
        },
        SQL`${SeriesReservationQ.expired_at} > NOW()`
      )})
    )`,

  // Note that this looks at soft-deleted registrations as well.
  getMostRecentRegistration: (
    userId: number | SQLFragment<number>
  ): SQLFragment<SeriesRegistrationAttributes> => SQL`
        ${SeriesRegistrationQ.getMostRecentRegistration({ seriesId: SeriesQ.id, userId })}
    `,

  isUserRegistered: (userId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`(NOT ${SeriesRegistrationQ.isWithdrawn()} AND ${SeriesRegistrationQ.deleted_at} IS NULL)
        ${SeriesQ.getMostRecentRegistration(userId)}
    `,

  isUserSelfReserved: (userId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SeriesReservationQ.where(
      {
        seriesId: SeriesQ.id,
        userId,
        reserverUserId: userId,
      },
      SQL`${SeriesReservationQ.expired_at} > NOW()`
    )})`,

  getSeriesTemplate: (): SQLFragment<SeriesTemplateAttributes> =>
    SeriesTemplateQ.where({ id: SeriesQ.series_template_id }),

  isHost: ({
    userId,
    approved = true,
  }: {
    userId: number | SQLFragment<number>
    // approved: null means we don't care about approval status
    approved?: boolean | null
  }): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SeriesHostQ.where({
      seriesId: SeriesQ.id,
      userId,
      approved,
    })})`,

  getHosts: ({
    approved = true,
    role,
  }: {
    approved?: boolean
    role?: HostRole | null
  }): SQLFragment<SeriesHostAttributes & UserAttributes> =>
    SeriesHostQ.where({
      seriesId: SeriesQ.id,
      approved,
      role,
      include: [
        {
          association: 'User',
          required: true,
        },
      ],
    }),

  countAllHosts: ({ approved }: { approved?: boolean }): SQLFragment<number> =>
    SQL`COUNT(*) ${SeriesHostQ.where({ seriesId: SeriesQ.id, approved })}`,

  isEnrollmentClosed: (): SQLFragment<boolean> =>
    SQL`(${SeriesQ.enrollment_closed_at} IS NOT NULL AND NOW() >= ${SeriesQ.enrollment_closed_at})`,

  countCompletedSessions: ({
    attemptedHosting,
  }: {
    attemptedHosting?: boolean
  } = {}): SQLFragment<number> =>
    SQL`COUNT(*) ${SessionQ.where(
      { seriesId: SeriesQ.id, attemptedHosting },
      SQL`${SessionQ.getComputedEndDate()} < NOW()`
    )}`,

  getRegisteredUsers: (): SQLFragment<UserAttributes> => SQL`FROM ${UserQ} WHERE ${UserQ.id} IN (
        SELECT ${SeriesRegistrationQ.user_id} ${SeriesRegistrationQ.where({
          seriesId: SeriesQ.id,
        })})`,

  isSummerCampSeries: (): SQLFragment<boolean> =>
    SQL`EXISTS (SELECT 1 ${SeriesTagQ.where(
      { seriesId: SeriesQ.id },
      SeriesTagQ.isSummerCampTag()
    )})`,

  hasUserRegistered: (userId: number | null | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SeriesRegistrationQ.where({
      seriesId: SeriesQ.id,
      userId,
    })})`,

  canUserAccessAssessments: (userId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`${SeriesQ.hasUserRegistered(userId)} OR ${SeriesQ.isHost({
      userId,
    })} OR EXISTS(SELECT 1 ${SessionQ.where(
      { seriesId: SeriesQ.id },
      SQL`${SessionQ.hasUserRegistered(userId)}`
    )})`,

  isSATSeries: ({ satTestId }: { satTestId?: number } = {}): SQLFragment<boolean> =>
    SQL`(
      ${SeriesQ.hasAnyTemplateSlug(SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS)}
      ${satTestId != null ? SQL`AND ${SeriesQ.getSATTestId()} = ${satTestId}` : SQL``}
    )`,

  isDialoguesSeries: (): SQLFragment<boolean> =>
    SQL`(
      ${SeriesQ.hasAnyTemplateSlug([...DIALOGUE_CLUB_TEMPLATE_SLUGS, DIALOGUE_EVENT_TEMPLATE_SLUG])}
    )`,

  getRegisteredSATBootcampSeries: (
    userId: number | SQLFragment<number>
  ): SQLFragment<SeriesAttributes> => SQL`
    ${SeriesQ.where(
      {},
      SQL`
        ${SeriesQ.hasUpcomingSession()} AND
        ${SeriesQ.isSATSeries()} AND
        (
          ${SeriesQ.id} IN (
              SELECT ${SeriesRegistrationQ.series_id}
              ${SeriesRegistrationQ.where({ userId, includeSeries: true })}
          )
        )
        `
    )}
  `,

  getScoreBandName: (): SQLFragment<SATScoreBandName | null> =>
    SQL`${SeriesQ.internal_state}->>'sat_bootcamp_score_band'`,

  getResearchGroup: (): SQLFragment<ResearchGroup | null> =>
    SQL`${SeriesQ.internal_state}->>'research_group'`,

  getSATTestId: (): SQLFragment<number | null> =>
    SQL`(${SeriesQ.internal_state}->>'sat_test_id')::INTEGER`,

  getGatingGroup: (): SQLFragment<GatingGroup | null> =>
    SQL`${SeriesQ.internal_state}->>'gating_group'`,

  hasResearchGroup: (group: ResearchGroup): SQLFragment<boolean> =>
    SQL`${SeriesQ.getResearchGroup()} = ${group}`,

  userMatchesSeriesResearchGroup: (user: UserContextType): SQLFragment<boolean> =>
    SQL`(
      (NOT ${SeriesQ.isSATSeries()})
      OR (${SeriesQ.isHost({ userId: user.id })})
      OR (${
        user.researchGroup == null
          ? SQL`TRUE` // TODO: Revert this to `${SeriesQ.getResearchGroup()} IS NULL` after the December 2024 SAT Bootcamp. For now, we're allowing non-research group users to sign up for any bootcamp
          : SQL`${SeriesQ.getResearchGroup()} = ${user.researchGroup}`
      })
    )`,

  hasGatingGroup: (group: GatingGroup): SQLFragment<boolean> =>
    SQL`${SeriesQ.getGatingGroup()} = ${group}`,

  hasTemplateSlug: (slug: string): SQLFragment<boolean> =>
    SQL`${SeriesQ.series_template_id} = (SELECT ${SeriesTemplateQ.id} FROM ${SeriesTemplateQ} WHERE ${SeriesTemplateQ.slug} = ${slug})`,

  isActiveOrEndedWithinDays: (days: number): SQLFragment<boolean> =>
    SQL`(${SeriesQ.closed_at} > NOW() - (INTERVAL '1 DAY' * ${days}) AND ${SeriesQ.closed_reason} IS NULL)`,

  hasAnyTemplateSlug: (slugs: string[]): SQLFragment<boolean> =>
    SQL`COALESCE(
      ${SeriesQ.series_template_id} = ANY(
        SELECT ${SeriesTemplateQ.id}
        FROM ${SeriesTemplateQ}
        WHERE ${SeriesTemplateQ.slug} = ANY(${slugs})
      ),
      FALSE
    )`,

  hasTemplateSlugPrefix: (prefix: string): SQLFragment<boolean> =>
    SQL`COALESCE(
      ${SeriesQ.series_template_id} = ANY(
        SELECT ${SeriesTemplateQ.id}
        FROM ${SeriesTemplateQ}
        WHERE ${SeriesTemplateQ.slug} LIKE ${`${prefix}%`}
      ),
      FALSE
    )`,

  isUpdatingDisabled: (): SQLFragment<boolean> =>
    SQL`COALESCE(${SeriesQ.internal_state}->>'disable_updates', 'false')::boolean`,

  userHasAttendedAtLeastOneSession: (userId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionAttendeeQ.where({
      userId,
      include: [
        {
          association: 'Session',
          required: true,
          where: SQL`${SessionQ.series_id} = ${SeriesQ.id}`,
        },
      ],
    })})`,

  getMostRecentlyAttendedSession: (userId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SessionAttendeeQ.where({
      userId,
      include: [
        {
          association: 'Session',
          required: true,
          where: SQL`${SessionQ.series_id} = ${SeriesQ.id} AND ${SessionQ.end_date} < NOW()`,
        },
      ],
      order: [[SessionQ.end_date, 'desc']],
      limit: 1,
    }),

  isCanceledWithoutHostedSessions: (): SQLFragment<boolean> =>
    SQL`${SeriesQ.closed_at} < NOW()
    AND ${SeriesQ.closed_reason} IS NOT NULL
    AND NOT EXISTS (SELECT 1 ${SessionQ.where({ seriesId: SeriesQ.id, attemptedHosting: true })})`,

  isWorldVisibility: (visibility: WorldVisibility): SQLFragment<boolean> => SQL`
    EXISTS(SELECT 1 ${WorldQ.where({ visibility, id: SeriesQ.world_id })})
  `,

  userCanViewSeries: (
    userId: number | SQLFragment<number>,
    {
      disableBucketCheck = false,
    }: {
      disableBucketCheck?: boolean
    } = {}
  ): SQLFragment<boolean> =>
    SQL`(
    ${SeriesQ.userCanViewWorldSeries(userId)}
    AND ${
      disableBucketCheck
        ? SQL`TRUE`
        : SQL`(
            ${UserQ.isBucketCompatible({ myUserId: userId, targetUserId: SeriesQ.user_id })}
            OR ${
              // We allow users in different buckets to co-host, so they should be able to
              // view their own series so long as they have been approved.
              SeriesQ.isHost({ userId, approved: true })
            }
          )`
    })`,

  /**
   * @deprecated: Use userCanViewSeries instead
   */
  userCanViewWorldSeries: (userId: number | SQLFragment<number>): SQLFragment<boolean> => SQL`
    (
      ${SeriesQ.world_id} IS NULL OR
      ${SeriesQ.isWorldVisibility('public')} OR
      ${SeriesQ.isWorldVisibility('unlisted')} OR
      EXISTS(
        SELECT 1 ${WorldMemberQ.where({
          worldId: SeriesQ.world_id,
          userId,
        })}
      )
    )`,

  isClosed: (): SQLFragment<boolean> =>
    SQL`(
      ${SeriesQ.closed_at} IS NOT NULL AND
      ${SeriesQ.closed_at} < NOW()
    )`,

  hasRequiredAttendanceSessions: (): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionQ.where({ seriesId: SeriesQ.id, attendancePolicy: 'required' })})`,

  isRecurringTemplateSeries: (): SQLFragment<boolean> =>
    SQL`(${SeriesQ.series_template_id} IS NOT NULL AND EXISTS(SELECT 1 ${SeriesTemplateQ.where(
      { id: SeriesQ.series_template_id },
      SQL`${SeriesTemplateQ.schedule_type} = ANY(${RECURRING_SERIES_TEMPLATE_SCHEDULE_TYPES.map(type => type)})`
    )}))`,

  hasRegisteredForSeriesWithTemplateSlugs: ({
    userId,
    templateSlugs,
  }: {
    userId: number | SQLFragment<number>
    templateSlugs: string[]
  }): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SeriesRegistrationQ.where({
      include: [
        {
          association: 'Series',
          required: true,
          include: [
            {
              association: 'SeriesTemplate',
              where: SQL`${SeriesTemplateQ.slug} = ANY(${templateSlugs})`,
              required: true,
            },
          ],
        },
      ],
      userId,
    })})`,

  countActiveTemplateRegistrationsByLearner: ({
    userId,
    seriesTemplateId,
  }: {
    userId: number
    seriesTemplateId: number
  }): SQLFragment<number> =>
    SQL`COUNT(*) ${SeriesRegistrationQ.where({
      include: [
        {
          association: 'Series',
          required: true,
          where: SQL`${
            SeriesQ.series_template_id
          } = ${seriesTemplateId} AND NOT ${SeriesQ.hasEnded()}`,
        },
      ],
      userId,
    })}`,

  getAverageNPSRating: (): SQLFragment<number | null> =>
    SQL`(SELECT AVG(${SessionFeedbackQ.nps}) ${SessionFeedbackQ.getFeedbackWithNPSInSeries()})`,

  getNPSRatingCount: (): SQLFragment<number> =>
    SQL`(SELECT COUNT(${SessionFeedbackQ.nps}) ${SessionFeedbackQ.getFeedbackWithNPSInSeries()})`,

  isTFABootcampSeries: (): SQLFragment<boolean> =>
    SQL`${SeriesQ.hasGatingGroup('2024-03-tfa-bootcamp')}`,

  isRequiredPhoneNumberSeries: (): SQLFragment<boolean> =>
    SQL`${SeriesQ.series_template_id} IS NOT NULL`,

  hasTag: (tag: SeriesTagType): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SeriesTagQ.where({ seriesId: SeriesQ.id, tag })})`,

  getTotalTutorSpeakingPercentage: (): SQLFragment<number> =>
    SQL`(
        SELECT ${SessionAttendeeQ.getTotalTutorSpeakingPercentage()}
        ${SessionAttendeeQ.where(
          {
            roles: [SessionAttendeeRole.Tutor, SessionAttendeeRole.Learner],
            include: [
              {
                association: 'Session',
                required: true,
                where: SQL`${SessionQ.series_id} = ${SeriesQ.id}`,
              },
            ],
          },
          SQL`${SessionAttendeeQ.speaking_duration_seconds} IS NOT NULL`
        )}
    )`,

  getSeriesForPage: ({
    id,
    userId,
    customWhere = null,
    disableBucketCheck = false,
  }: {
    id: number
    userId: number
    customWhere?: SQLFragment<unknown> | null
    disableBucketCheck?: boolean
  }): SQLFragment<SeriesAttributes> =>
    SeriesQ.where(
      {
        id,
        includeAllResearchGroups: true,
        paranoid: false,
      },
      SQL`
      ${SeriesQ.userCanViewSeries(userId, { disableBucketCheck })}
      ${customWhere != null ? SQL`AND (${customWhere})` : SQL``}
      `
    ),

  getSeriesTemplateSlug: (): SQLFragment<string | null> =>
    SQL`(SELECT ${SeriesTemplateQ.slug} ${SeriesQ.getSeriesTemplate()})`,

  countActiveSeriesForTemplate({
    seriesTemplateId,
  }: {
    seriesTemplateId: number | SQLFragment<number>
  }): SQLFragment<number> {
    return SQL`COUNT(*) ${SeriesQ.where(
      {
        seriesTemplateId,
      },
      SQL`
        NOT (${SeriesQ.isClosed()})
      `
    )}`
  },

  // Once we have programs in the database, we can just check if
  // SeriesQ.program_iteration_id matches the iteration id.
  // Till then, we rely on there being series templates being
  // unique across program iterations, which we want to move away
  // from and have done so for SAT.
  isOfferingForProgramIteration: (
    iteration: ProgramIteration,
    offeringFilter?: (offering: ProgramOfferingInstance) => boolean
  ): SQLFragment<boolean> => {
    const { startDate, offerings } = iteration

    return SQL`(
      (SELECT ${SeriesQ.getComputedStartDate()}) >= ${startDate.toISODate()}
      AND (SELECT ${SeriesTemplateQ.slug} ${SeriesTemplateQ.where({ id: SeriesQ.series_template_id })}) = ANY(${offerings
        .filter(offeringFilter != null ? offeringFilter : () => true)
        .map(offering => offering.seriesTemplateSlug)})
      )`
  },

  getPastSessionsHostedBy: ({
    userId,
    requireLearnerAttendance,
  }: {
    userId: number | SQLFragment<number>
    requireLearnerAttendance: boolean
  }): SQLFragment<SessionAttributes> =>
    SessionQ.where(
      {
        seriesId: SeriesQ.id,
        include: [
          {
            association: 'Topic',
            required: true,
          },
        ],
      },
      SessionQ.hasBeenHostedBy(
        {
          userId,
          requireLearnerAttendance,
        },
        SessionQ.hasEnded()
      )
    ),

  getPastParticipatedSessions: ({
    userId,
  }: {
    userId: number | SQLFragment<number>
  }): SQLFragment<SessionAttributes> =>
    SessionQ.where(
      {
        seriesId: SeriesQ.id,
        include: [
          {
            association: 'Topic',
            required: true,
          },
        ],
      },
      SQL`${SessionQ.id} IN (
        SELECT sessions_union.id FROM (
          SELECT ${SessionQ.id} AS id
            ${UserQ.getAttendedSessions({ userId, customWhere: SessionQ.hasEnded() })}
          UNION
          SELECT ${SessionQ.id} AS id
            ${UserQ.getTutoringSessions({
              userId,
              requireLearnerAttendance: true,
              customWhere: SessionQ.hasEnded(),
            })}
        ) AS sessions_union
      )`
    ),

  hasPastSessionHostedBy: ({
    userId,
    requireLearnerAttendance,
  }: {
    userId: number | SQLFragment<number>
    requireLearnerAttendance: boolean
  }): SQLFragment<boolean> =>
    SQL`EXISTS (SELECT 1 ${SeriesQ.getPastSessionsHostedBy({
      userId,
      requireLearnerAttendance,
    })})`,

  hasPastParticipatedSession: ({
    userId,
  }: {
    userId: number | SQLFragment<number>
  }): SQLFragment<boolean> =>
    SQL`EXISTS (SELECT 1 ${SeriesQ.getPastParticipatedSessions({
      userId,
    })})`,

  getLearnerFeedback: (
    seriesId: number | SQLFragment<number>,
    revieweeUserId?: number | SQLFragment<number>
  ): SQLFragment<LearnerFeedbackAttributes> =>
    LearnerFeedbackQ.where({
      type: LEARNER_FEEDBACK_TYPE_TUTOR_TO_LEARNER,
      ...(revieweeUserId != null ? { revieweeUserId } : {}),
      include: [
        {
          association: 'Session',
          required: true,
          where: SQL`${SessionQ.series_id} = ${seriesId}`,
        },
      ],
    }),
}

export default SeriesQ
